import React, { useEffect, useRef } from 'react';
import { useChatStore, type ContextItem } from '../../store/chatStore';
import { MarkdownWidget } from '../MessageContent/markdown/MarkdownWidget';
import { FindingsContext } from './FindingsContext';
import { AssetContext } from './AssetContext';
import { RiskContext } from './RiskContext';
import { VulnerabilityContext } from './VulnerabilityContext';

import { useTranslation } from 'react-i18next';

// ContextBadge component for beautiful context chips
const ContextBadge: React.FC<{
  icon?: React.ReactNode;
  display: React.ReactNode;
  prompt: string;
  onRemove: () => void;
  type?: string;
}> = ({ icon, display, prompt, onRemove }) => {
  const { t } = useTranslation('pleri/chat');
  // Consistent neutral style matching widget preview cards
  return (
    <div
      className="flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-900 shadow-sm transition-all duration-150 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
      title={prompt}
    >
      {icon && <span className="mr-1 flex items-center">{icon}</span>}
      <span className="max-w-[120px] truncate">{display}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-black"
        title={t('common:remove')}
      >
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

// Enhanced context renderer that can show widgets or badges
const ContextRenderer: React.FC<{
  item: ContextItem;
  onRemove: () => void;
}> = ({ item, onRemove }) => {
  const { t } = useTranslation('pleri/chat');
  // Delegate to dedicated context components by type
  if (item.type === 'findings') {
    return <FindingsContext item={item} onRemove={onRemove} />;
  }
  if (item.type === 'asset') {
    return <AssetContext item={item} onRemove={onRemove} />;
  }
  if (item.type === 'risk') {
    return <RiskContext item={item} onRemove={onRemove} />;
  }
  if (item.type === 'vulnerability') {
    return <VulnerabilityContext item={item} onRemove={onRemove} />;
  }

  // Extract widget config from ```widget``` code blocks
  const extractWidgetConfig = (prompt: string): string | null => {
    const widgetMatch = prompt.match(/```widget\n([\s\S]*?)\n```/);
    return widgetMatch ? widgetMatch[1] : null;
  };

  // Check if this is a widget config context item
  if (item.type === 'widget_config') {
    const widgetConfig = extractWidgetConfig(item.prompt);

    if (widgetConfig) {
      return (
        <div className="group relative">
          {/* Widget preview matching attached files style */}
          <div className="inline-flex max-w-[20rem] min-w-[14rem] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            {/* Header matching file preview style */}
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900">
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {item.display}
              </span>
              <button
                type="button"
                onClick={onRemove}
                className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-black"
                title={t('common:remove')}
              >
                <svg
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Widget preview scaled down */}
            <div className="overflow-x-hidden overflow-y-auto border-t border-gray-100 dark:border-gray-800">
              <div
                className="origin-top-left scale-50 transform"
                style={{
                  width: '200%',
                  height: '100px', // Fixed height to prevent overflow
                  maxHeight: '100px',
                }}
              >
                <MarkdownWidget code={widgetConfig} />
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // Default badge rendering for non-widget context items
  return (
    <ContextBadge
      icon={item.icon}
      display={item.display}
      prompt={item.prompt}
      onRemove={onRemove}
      type={item.type}
    />
  );
};

interface ContextPreviewProps {
  // No props needed - component manages its own state
}

export const ContextPreview: React.FC<ContextPreviewProps> = () => {
  const { t } = useTranslation('pleri/chat');
  const attachedContextItems = useChatStore(
    (state) => state.attachedContextItems,
  );
  const removeContextItem = useChatStore((state) => state.removeContextItem);
  const setAttachedContextItems = useChatStore(
    (state) => state.setAttachedContextItems,
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevItemsLengthRef = useRef(attachedContextItems.length);

  // Auto-scroll to the right when new items are added
  useEffect(() => {
    if (attachedContextItems.length > prevItemsLengthRef.current) {
      // New item added, scroll to the right
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          left: scrollContainerRef.current.scrollWidth,
          behavior: 'smooth',
        });
      }
    }
    prevItemsLengthRef.current = attachedContextItems.length;
  }, [attachedContextItems.length]);

  if (attachedContextItems.length === 0) {
    return null;
  }

  const totalItems = attachedContextItems.length;

  return (
    <div className="mb-1">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg
            className="h-4 w-4 text-gray-600 dark:text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a.997.997 0 01-1.414 0l-7-7A1.997 1.997 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {t('attachedContext', { totalItems })}
          </span>
        </div>
        {totalItems > 0 && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 shadow-sm hover:bg-gray-50 active:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
            onClick={() => {
              setAttachedContextItems([]);
            }}
            title={t('clearAllContext')}
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            {t('clearAll')}
          </button>
        )}
      </div>
      <div
        ref={scrollContainerRef}
        className="custom-scrollbar overflow-x-auto pr-1"
      >
        <div className="flex items-center gap-2 py-1">
          {attachedContextItems.map((item) => (
            <ContextRenderer
              key={item.id}
              item={item}
              onRemove={() => removeContextItem(item.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContextPreview;
